import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/card';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

/**
 * P&L Comparison Chart Component
 * Displays visual comparison of financial metrics between two periods
 */
export const PLComparisonChart = ({ 
  profitLossData, 
  plCompareData, 
  calcVariance, 
  formatVariance 
}) => {
  if (!profitLossData || !plCompareData) return null;

  const financialMetricsData = [
    {
      name: 'Revenue',
      current: profitLossData.income.revenue_ex_gst,
      previous: plCompareData.income.revenue_ex_gst,
    },
    {
      name: 'Cost of Sales',
      current: (profitLossData.cost_of_goods_sold?.total_cost_of_sales || profitLossData.cost_of_goods_sold?.stock_cost + profitLossData.expenses.stock_purchases),
      previous: (plCompareData.cost_of_goods_sold?.total_cost_of_sales || plCompareData.cost_of_goods_sold?.stock_cost + plCompareData.expenses.stock_purchases),
    },
    {
      name: 'Operating Exp',
      current: profitLossData.expenses.expenses_ex_gst,
      previous: plCompareData.expenses.expenses_ex_gst,
    },
  ];

  const profitComparisonData = [
    {
      name: 'Gross Profit',
      current: profitLossData.summary.gross_profit,
      previous: plCompareData.summary.gross_profit,
    },
    {
      name: 'Net Profit',
      current: profitLossData.summary.net_profit,
      previous: plCompareData.summary.net_profit,
    },
  ];

  const varianceCards = [
    { label: 'Revenue Change', current: profitLossData.income.revenue_ex_gst, prev: plCompareData.income.revenue_ex_gst, isProfit: true },
    { label: 'Expense Change', current: profitLossData.expenses.expenses_ex_gst, prev: plCompareData.expenses.expenses_ex_gst, isProfit: false },
    { label: 'Gross Profit Δ', current: profitLossData.summary.gross_profit, prev: plCompareData.summary.gross_profit, isProfit: true },
    { label: 'Net Profit Δ', current: profitLossData.summary.net_profit, prev: plCompareData.summary.net_profit, isProfit: true },
  ];

  return (
    <Card style={{ border: '1px solid #e5e7eb' }}>
      <CardHeader>
        <CardTitle style={{ fontSize: '1rem' }}>Period Comparison Chart</CardTitle>
        <CardDescription>Visual comparison of key financial metrics</CardDescription>
      </CardHeader>
      <CardContent>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
          {/* Financial Metrics Chart */}
          <div>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '1rem', textAlign: 'center' }}>
              Financial Metrics Comparison
            </h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={financialMetricsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                <Tooltip 
                  formatter={(value) => [`$${value.toFixed(2)}`, '']}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
                <Legend />
                <Bar dataKey="current" name="Current Period" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="previous" name="Previous Period" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Profit Comparison Chart */}
          <div>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginBottom: '1rem', textAlign: 'center' }}>
              Profit Comparison
            </h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={profitComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                <Tooltip 
                  formatter={(value) => [`$${value.toFixed(2)}`, '']}
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                />
                <Legend />
                <Bar dataKey="current" name="Current Period" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="previous" name="Previous Period" fill="#94a3b8" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Variance Indicator Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '1rem', 
          marginTop: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8fafc',
          borderRadius: '8px'
        }}>
          {varianceCards.map((item, idx) => {
            const v = calcVariance(item.current, item.prev);
            const f = formatVariance(v, item.isProfit);
            return (
              <div key={idx} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>{item.label}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: f.color }}>
                  {f.arrow} {f.pctText}
                </div>
                <div style={{ fontSize: '0.75rem', color: f.color }}>{f.diffText}</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default PLComparisonChart;
